import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as panelSelectors } from 'builder/modules/panel'
import { DocumentTypes } from 'builder/modules/constants'
import { actions as resumeDistributionActions } from 'builder/modules/resumeDistribution'
import { AvailableCountriesBanner } from '../AvailableCountriesBanner/AvailableCountriesBanner'
import { LOCALIZATION_KEY_VER_4, RESUME_DISTRIBUTION_WELCOME_SCREEN_SHOWN } from '../../constants'
import Image from '../../assets/rd_welcome_v2.png'
import ImageNoResume from '../../assets/rd_welcome_v2_no_resume.png'
import { ResumeSelectModal } from '../ResumeSelectModal/ResumeSelectModal'
import {
  Container,
  IconPeople,
  HeroContainer,
  HeroInformationWrapper,
  HeroGhostTitle,
  HeroTitle,
  HeroDescription,
  BenefitsContainer,
  BenefitWrapper,
  BenefitIconWrapper,
  BenefitTitle,
  BenefitDescription,
  HeroImageWrapper,
  HeroImage,
  IconTarget,
  IconHeartMessage,
  ContinueButton,
} from './styles'

type Props = {
  handleUploadClick: (event: React.MouseEvent<HTMLInputElement>) => void
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const LANGUAGE_KEY = `${LOCALIZATION_KEY_VER_4}.welcome_screen`

export const WithoutResumeScreenV2 = (props: Props) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const [isResumeSelectModalOpen, setIsResumeSelectModalOpen] = useState<boolean>(false)
  const { handleUploadClick, handleFileInputChange } = props

  const resumeList = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.resume),
  )
  const trDocuments = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.trDocument),
  )

  const trResumes = useMemo(
    () =>
      Array.isArray(trDocuments)
        ? trDocuments.filter(item => item.doc_type === DocumentTypes.resume)
        : [],
    [trDocuments],
  )

  const RD_BENEFITS = [
    {
      icon: <IconPeople />,
      title: i18n.t(`${LANGUAGE_KEY}.benefits.benefit_1_title`),
      description: i18n.t(`${LANGUAGE_KEY}.benefits.benefit_1_description`),
    },
    {
      icon: <IconTarget />,
      title: i18n.t(`${LANGUAGE_KEY}.benefits.benefit_2_title`),
      description: i18n.t(`${LANGUAGE_KEY}.benefits.benefit_2_description`),
    },
    {
      icon: <IconHeartMessage />,
      title: i18n.t(`${LANGUAGE_KEY}.benefits.benefit_3_title`),
      description: i18n.t(`${LANGUAGE_KEY}.benefits.benefit_3_description`),
    },
  ]

  const hasResumes = useMemo(() => {
    return (resumeList?.length ?? 0) > 0 || (trResumes?.length ?? 0) > 0
  }, [resumeList, trResumes])

  const handleContinue = useCallback(() => {
    trackInternalEvent('click_continue', { label: 'rd_welcome_screen' })

    if (hasResumes) {
      const getLatestItem = () => {
        const latestResume = resumeList?.reduce((latest, current) => {
          return new Date(current.updatedAt ?? '') > new Date(latest.updatedAt ?? '')
            ? current
            : latest
        }, resumeList?.[0])

        const latestTrDoc = trResumes?.reduce((latest, current) => {
          return new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        }, trResumes?.[0])

        if (!latestResume) return latestTrDoc
        if (!latestTrDoc) return latestResume

        return new Date(latestResume.updatedAt ?? '') > new Date(latestTrDoc.created_at)
          ? latestResume
          : latestTrDoc
      }

      const latestDocument = getLatestItem()
      localStorage.setItem(RESUME_DISTRIBUTION_WELCOME_SCREEN_SHOWN, 'true')
      dispatch(resumeDistributionActions.setResumeDetails(latestDocument))
    } else {
      setIsResumeSelectModalOpen(true)
    }
  }, [dispatch, hasResumes, resumeList, trResumes])

  return (
    <Container>
      <AvailableCountriesBanner />
      <HeroContainer>
        <HeroInformationWrapper>
          <HeroGhostTitle>{i18n.t(`${LANGUAGE_KEY}.title`)}</HeroGhostTitle>
          <HeroTitle>{i18n.t(`${LANGUAGE_KEY}.hero_title`)}</HeroTitle>
          <HeroDescription>{i18n.t(`${LANGUAGE_KEY}.description`)}</HeroDescription>
          <ContinueButton onClick={handleContinue}>
            {i18n.t(`${LANGUAGE_KEY}.hero_button_text`)}
          </ContinueButton>
        </HeroInformationWrapper>
        <HeroImageWrapper>
          <HeroImage src={hasResumes ? Image : ImageNoResume} />
        </HeroImageWrapper>
      </HeroContainer>
      <BenefitsContainer>
        {RD_BENEFITS?.map((item, index) => {
          return (
            <BenefitWrapper key={index}>
              <BenefitIconWrapper>{item.icon}</BenefitIconWrapper>
              <BenefitTitle>{item.title}</BenefitTitle>
              <BenefitDescription>{item.description}</BenefitDescription>
            </BenefitWrapper>
          )
        })}
      </BenefitsContainer>
      {isResumeSelectModalOpen && (
        <ResumeSelectModal
          handleUploadClick={handleUploadClick}
          handleFileInputChange={handleFileInputChange}
          onClose={() => setIsResumeSelectModalOpen(false)}
        />
      )}
    </Container>
  )
}
